import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  PageHeader,
  SectionCard,
  StatusBadge,
  InfoRow,
  WarningBanner,
  EmptyState,
} from "./shared/components";
import {
  getFinalDetails,
  generateAcceptanceLetter,
  generateServiceCertificate,
  generateRelievingOrder,
  generateNoDuesCertificate,
  generateWithdrawalOrder,
  formatDate,
  formatINR,
  getCurrentEmpNo,
  FinalDetails as FinalDetailsModel,
} from "./services/eSeparation.service";

const DOC_ICONS: Record<string, string> = {
  relieving_letter: "ti-logout",
  service_certificate: "ti-award",
  no_dues_certificate_doc: "ti-circle-check",
  acceptance_letter: "ti-file-check",
  acknowledgment_letter: "ti-file-text",
  withdrawal_order: "ti-file-certificate",
};

const FinalDetails: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const stateReq = (location.state as any)?.request;
  const empNo =
    stateReq?.emp_no || (location.state as any)?.emp_no || getCurrentEmpNo();

  const [details, setDetails] = useState<FinalDetailsModel | null>(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState<Record<string, boolean>>({});

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    getFinalDetails(empNo)
      .then((res) => {
        if (mounted) setDetails(res.data || null);
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [empNo]);

  const isCompleted =
    details?.no_dues_status === "Completed" ||
    String(details?.no_dues_status).toUpperCase() === "CLEARED";

  const handleDownload = async (docType: string, docName: string, available: boolean) => {
    if (!available) {
      toast.warning(
        `${docName} is not yet available. It will be generated once HR Admin / the Competent Authority has acted.`
      );
      return;
    }
    const requestId = details?.request_id;
    if (!requestId) {
      toast.error("Request ID not found.");
      return;
    }
    setDownloading((p) => ({ ...p, [docType]: true }));
    let res;
    switch (docType) {
      case "acceptance_letter":
        res = await generateAcceptanceLetter(requestId);
        break;
      case "service_certificate":
        res = await generateServiceCertificate(requestId);
        break;
      case "relieving_letter":
        res = await generateRelievingOrder(requestId);
        break;
      case "no_dues_certificate_doc":
        res = await generateNoDuesCertificate(requestId);
        break;
      case "withdrawal_order":
        res = await generateWithdrawalOrder(requestId);
        break;
      default:
        res = { success: false, message: "Unknown document type." };
    }
    setDownloading((p) => ({ ...p, [docType]: false }));
    if (res.success) toast.success(`${docName} downloaded successfully.`);
    else toast.error(res.message || `Failed to download ${docName}.`);
  };

  let idx = 0;

  return (
    <div className="page-wrapper">
      <div className="content">
        <PageHeader
          title="Final Separation Details"
          subtitle="Complete separation summary and documents"
          right={<StatusBadge status={details?.no_dues_status || "Pending"} />}
          onExit={() => navigate(-1)}
        />

        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status" />
            <div className="text-muted small mt-2">Loading Final Details...</div>
          </div>
        ) : !details ? (
          <EmptyState
            icon="ti-archive"
            title="No Approved Separation Request"
            message="Final details will be available once your resignation has been approved and completed."
          />
        ) : (
          <>
            {details.is_withdrawal && (
              <WarningBanner
                type="success"
                message="Your request to withdraw your resignation has been approved. Your resignation stands withdrawn and you continue in service. No acceptance letter or certificates are issued — your Withdrawal Order is available below."
              />
            )}

            {!details.is_withdrawal && !isCompleted && (
              <WarningBanner
                type="info"
                message="Final details will be fully available after your resignation is approved and the No-Dues process is completed."
              />
            )}

            {details.docs_pending_release && (
              <WarningBanner
                type="info"
                message="Your separation is fully approved and cleared. The Relieving Order and Service Certificate will be available once the HR Assistant gives the final release."
              />
            )}

            {/* SECTION 1: Employee Summary */}
            <SectionCard index={idx++} title="Employee Summary">
              <InfoRow label="Employee Name" value={details.emp_name} />
              <InfoRow label="Employee No." value={details.emp_no} />
              <InfoRow label="Designation" value={details.designation} />
              <InfoRow label="Department" value={details.department} />
              <InfoRow label="Date of Joining" value={formatDate(details.doj)} />
            </SectionCard>

            {/* A withdrawn resignation: a short summary instead of the separation /
                settlement sections (there is no separation to settle). */}
            {details.is_withdrawal && (
              <SectionCard index={idx++} title="Withdrawal Summary">
                <InfoRow label="Resignation Intimation Date" value={formatDate(details.intimation_date)} />
                <InfoRow label="Withdrawal Requested On" value={formatDate(details.withdrawal_date)} />
                <InfoRow label="Reason for Withdrawal" value={details.withdrawal_reason || "—"} />
                <InfoRow label="Outcome" value="Withdrawal approved — you continue in service" valueColor="#049123" bold />
              </SectionCard>
            )}

            {!details.is_withdrawal && (
              <>
            {/* SECTION 2: Resignation Summary */}
            <SectionCard index={idx++} title="Resignation Summary">
              <InfoRow label="Resignation Type" value={details.resignation_type} bold />
              <InfoRow label="Intimation Date" value={formatDate(details.intimation_date)} />
              <InfoRow label="Notice End Date" value={formatDate(details.notice_end_date)} />
              <InfoRow
                label="Approved Relieving Date"
                value={formatDate(details.approved_relieving_date)}
                valueColor="#0F6E56"
                bold
              />
              <InfoRow label="Competent Authority" value={details.competent_authority} />
            </SectionCard>

            {/* SECTION 3: Short Notice Summary */}
            {details.short_notice_applied && (
              <SectionCard index={idx++} title="Short Notice Summary">
                <InfoRow label="Short Notice Applied" value="Yes" valueColor="#BA7517" />
                <InfoRow
                  label="Shortfall Days"
                  value={details.shortfall_days ? `${details.shortfall_days} days` : "—"}
                />
                <InfoRow
                  label="Amount Deducted"
                  value={details.short_notice_deducted ? formatINR(details.short_notice_deducted) : "—"}
                  valueColor="#C0282C"
                  bold
                />
              </SectionCard>
            )}

            {/* SECTION 4: Bond & Waiver Summary */}
            <SectionCard index={idx++} title="Bond & Waiver Summary">
              <InfoRow label="Bond Status" value={details.bond_status} />
              {details.waiver_status && (
                <InfoRow
                  label="Waiver Status"
                  value={details.waiver_status}
                  valueColor={details.waiver_status === "Approved" ? "#049123" : "#BA7517"}
                />
              )}
            </SectionCard>

            {/* SECTION 5: No-Dues Summary */}
            <SectionCard index={idx++} title="No-Dues Summary">
              <InfoRow
                label="No-Dues Status"
                value={details.no_dues_status}
                valueColor={isCompleted ? "#049123" : "#BA7517"}
                bold
              />
            </SectionCard>

            {/* SECTION 6: Financial Settlement */}
            <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: 16, overflow: "hidden" }}>
              <div style={{ height: 4, background: "#993C1D" }} />
              <div className="card-body">
                <h5 className="fw-semibold mb-3">Financial Settlement</h5>
                <InfoRow
                  label="Total Recoverable"
                  value={formatINR(details.total_recoverable)}
                  valueColor="#C0282C"
                />
                <InfoRow
                  label="Total Payable"
                  value={formatINR(details.total_payable)}
                  valueColor="#049123"
                />
                <div className="d-flex justify-content-between align-items-center pt-3 mt-2 border-top">
                  <span className="fw-bold">Net Settlement</span>
                  <span
                    className="fw-bold"
                    style={{
                      color: details.net_settlement >= 0 ? "#049123" : "#C0282C",
                      fontSize: 16,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {formatINR(Math.abs(details.net_settlement))}
                    {details.net_settlement < 0 ? " (Due)" : " (Payable)"}
                  </span>
                </div>
              </div>
            </div>

              </>
            )}

            {/* SECTION 7: Documents */}
            <div className="d-flex align-items-center gap-2 mb-3">
              <span
                className="text-uppercase text-muted fw-semibold"
                style={{ fontSize: 11, letterSpacing: 1.2 }}
              >
                Downloadable Documents
              </span>
              <span className="flex-grow-1 border-bottom" />
            </div>

            <div className="row g-3 mb-4">
              {details.documents.map((doc, i) => (
                <div className="col-md-4 col-sm-6" key={i}>
                  <button
                    type="button"
                    className="card border-0 shadow-sm w-100 h-100 text-center p-3"
                    style={{ borderRadius: 14, opacity: doc.available ? 1 : 0.6, cursor: "pointer" }}
                    onClick={() => handleDownload(doc.doc_type, doc.doc_name, doc.available)}
                  >
                    <span
                      className="d-inline-flex align-items-center justify-content-center mx-auto mb-2"
                      style={{
                        width: 44,
                        height: 44,
                        borderRadius: 12,
                        background: doc.available ? "#E6F1FB" : "rgba(0,0,0,0.05)",
                        color: doc.available ? "#185FA5" : "#9aa0a6",
                      }}
                    >
                      {downloading[doc.doc_type] ? (
                        <span className="spinner-border spinner-border-sm" role="status" />
                      ) : (
                        <i className={`ti ${DOC_ICONS[doc.doc_type] || "ti-file"}`} style={{ fontSize: 20 }} />
                      )}
                    </span>
                    <div className="fw-semibold small">{doc.doc_name}</div>
                    {doc.available ? (
                      <span
                        className="d-inline-flex align-items-center gap-1 mt-2 px-2 py-1 rounded-pill mx-auto"
                        style={{ background: "#E6F1FB", color: "#185FA5", fontSize: 11 }}
                      >
                        <i className="ti ti-download" /> PDF
                      </span>
                    ) : (
                      <span className="text-muted mt-2 d-block" style={{ fontSize: 11 }}>
                        Pending
                      </span>
                    )}
                  </button>
                </div>
              ))}
            </div>

            {isCompleted && (
              <div
                className="d-flex align-items-start gap-2 rounded p-3 mb-3"
                style={{ background: "rgba(0,0,0,0.03)" }}
              >
                <i className="ti ti-lock text-muted mt-1" />
                <div className="small text-muted">
                  This record is permanently locked. All fields are read-only. Any
                  corrections must follow the authorized rectification workflow.
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default FinalDetails;
